import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ShieldCheck, Plus, Loader2, ChevronRight, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORIES = [
  { value: 'safety', label: 'Safety' },
  { value: 'behaviour', label: 'Inappropriate Behaviour' },
  { value: 'privacy', label: 'Privacy' },
  { value: 'other', label: 'Other' },
];

const STATUS_STYLE = {
  submitted: 'bg-amber-50 text-amber-700',
  under_review: 'bg-blue-50 text-blue-700',
  closed: 'bg-gray-100 text-gray-600',
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const EmergencyNotice = () => (
  <div className="rounded-2xl border border-[#FBD9D2] bg-[#FFF7F5] px-4 py-3 flex gap-3" data-testid="emergency-notice">
    <AlertTriangle className="h-4 w-4 text-[#F97D6A] shrink-0 mt-0.5" />
    <p className="text-xs text-[#9A3412] leading-relaxed">
      <strong>If a child is at immediate risk, contact the school directly or your local emergency
      services first.</strong> This form is monitored by the school's safeguarding coordinators during
      working hours and is not an emergency service.
    </p>
  </div>
);

export default function ParentSafetyConcerns() {
  const [reports, setReports] = useState(null);
  const [children, setChildren] = useState([]);
  const [contact, setContact] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [openReport, setOpenReport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const blank = { child_id: '', category: '', description: '', incident_date: '', related_activity_id: '' };
  const [form, setForm] = useState(blank);
  const [activities, setActivities] = useState([]);

  const load = useCallback(async () => {
    const [r, c, k] = await Promise.all([
      api.get('/safeguarding/my-reports'),
      api.get('/safeguarding/my-children'),
      api.get('/safeguarding/contact').catch(() => ({ data: null })),
    ]);
    setReports(r.data);
    setChildren(c.data);
    setContact(k.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const pickChild = async (childId) => {
    setForm(f => ({ ...f, child_id: childId, related_activity_id: '' }));
    try {
      const res = await api.get('/safeguarding/related-activities', { params: { child_id: childId } });
      setActivities(res.data);
    } catch { setActivities([]); }
  };

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const activity = activities.find(a => a.id === form.related_activity_id);
      const res = await api.post('/safeguarding/my-reports', {
        child_id: form.child_id,
        category: form.category,
        description: form.description,
        incident_date: form.incident_date,
        related_activity_id: form.related_activity_id,
        related_activity_label: activity?.label || '',
        related_activity_type: activity?.type || '',
      });
      setFormOpen(false);
      setForm(blank);
      setActivities([]);
      await load();
      setOpenReport(res.data);
      toast.success(res.data.duplicate
        ? `This concern was already submitted as ${res.data.reference}`
        : `Concern submitted — reference ${res.data.reference}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit the concern');
    }
    setSaving(false);
  };

  const sendFollowUp = async (requestReview) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.post(`/safeguarding/my-reports/${openReport.id}/additional-info`,
        { text: followUp, request_review: requestReview });
      setOpenReport(res.data);
      setFollowUp('');
      await load();
      toast.success(requestReview ? 'Further review requested' : 'Information added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add the information');
    }
    setSaving(false);
  };

  const openDetail = async (id) => {
    const res = await api.get(`/safeguarding/my-reports/${id}`);
    setOpenReport(res.data);
    setFollowUp('');
  };

  const canSubmit = form.child_id && form.category && form.description.trim().length >= 10;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="safety-concerns-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight">Safety Concerns</h1>
          <p className="text-sm text-[#6B7280] mt-1.5 max-w-xl leading-relaxed">
            Raise a concern about your child's experience on CareersPOV. Your school's safeguarding
            coordinators handle every report confidentially.
          </p>
        </div>
        <Button
          onClick={() => setFormOpen(true)}
          className="rounded-xl h-11 bg-[#F97D6A] hover:bg-[#e86c5a] text-white font-semibold w-full sm:w-auto"
          data-testid="report-concern-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Report Safety Concern
        </Button>
      </div>

      <div className="space-y-4">
        <EmergencyNotice />

        {contact?.contact_email && (
          <div className="rounded-2xl border border-[#EFF0F2] bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid="school-contact">
            <span className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" /> School contact
            </span>
            <span className="text-sm text-[#1F2937] break-words">
              {contact.contact_name || contact.school_name}
              {contact.contact_email ? ` · ${contact.contact_email}` : ''}
              {contact.contact_mobile ? ` · ${contact.contact_mobile}` : ''}
            </span>
          </div>
        )}

        {reports === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-[#9CA3AF]" /></div>
        ) : reports.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#EFF0F2] p-10 text-center" data-testid="no-reports">
            <div className="h-11 w-11 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            </div>
            <p className="text-sm font-semibold text-[#1F2937]">No concerns reported</p>
            <p className="text-sm text-[#6B7280] mt-1.5 max-w-sm mx-auto leading-relaxed">
              If something about a session, workshop or internship worries you, report it here and your
              school will look into it.
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="reports-list">
            {reports.map(r => (
              <button
                key={r.id}
                onClick={() => openDetail(r.id)}
                className="w-full text-left bg-white rounded-2xl border border-[#EFF0F2] px-4 py-3.5 hover:border-[#F97D6A]/40 transition-colors flex items-center gap-3"
                data-testid={`report-row-${r.reference}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-[#1F2937]">{r.reference}</span>
                    <Badge className={`border-0 text-[10px] ${STATUS_STYLE[r.status]}`} data-testid={`report-status-${r.reference}`}>
                      {r.status_label}
                    </Badge>
                  </div>
                  <p className="text-xs text-[#6B7280] mt-1 break-words">
                    {r.child_name} · {r.category_label} · submitted {fmt(r.created_at)}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-[#9CA3AF] shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Report a concern */}
      <Dialog open={formOpen} onOpenChange={(o) => { if (!o) { setFormOpen(false); setForm(blank); setActivities([]); } }}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Report a safety concern</DialogTitle>
            <DialogDescription>
              Only your school's safeguarding coordinators can read this. Your name, your school and
              the time are recorded automatically.
            </DialogDescription>
          </DialogHeader>

          <EmergencyNotice />

          <div className="space-y-3.5">
            <div>
              <Label>Child *</Label>
              <Select value={form.child_id} onValueChange={pickChild}>
                <SelectTrigger className="mt-1 rounded-xl h-11 text-base sm:text-sm border-gray-200" data-testid="concern-child-select">
                  <SelectValue placeholder="Select your child" />
                </SelectTrigger>
                <SelectContent>
                  {children.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name}{c.grade ? ` · Grade ${c.grade}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {activities.length > 0 && (
              <div>
                <Label>Related session, workshop or internship</Label>
                <Select value={form.related_activity_id} onValueChange={(v) => setForm(f => ({ ...f, related_activity_id: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl h-11 text-base sm:text-sm border-gray-200" data-testid="concern-activity-select">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    {activities.map(a => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="mt-1 rounded-xl h-11 text-base sm:text-sm border-gray-200" data-testid="concern-category-select">
                  <SelectValue placeholder="What is this about?" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Date of the incident</Label>
              <Input type="date" value={form.incident_date} onChange={(e) => setForm(f => ({ ...f, incident_date: e.target.value }))}
                     className="mt-1 rounded-xl h-11 text-base sm:text-sm border-gray-200" data-testid="concern-date-input" />
            </div>

            <div>
              <Label>What happened? *</Label>
              <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                        rows={5} placeholder="Describe the concern in your own words"
                        className="mt-1 rounded-xl text-base sm:text-sm border-gray-200" data-testid="concern-description-input" />
              <p className="text-[11px] text-[#9CA3AF] mt-1">At least 10 characters.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setFormOpen(false); setForm(blank); }} className="rounded-xl h-11 sm:h-10">Cancel</Button>
            <Button onClick={submit} disabled={!canSubmit || saving}
                    className="rounded-xl h-11 sm:h-10 bg-[#F97D6A] hover:bg-[#e86c5a] text-white"
                    data-testid="submit-concern-btn">
              {saving ? 'Submitting…' : 'Submit concern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* My report */}
      <Dialog open={!!openReport} onOpenChange={(o) => !o && setOpenReport(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-detail-dialog">
          {openReport && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Outfit'] flex items-center gap-2 flex-wrap">
                  {openReport.reference}
                  <Badge className={`border-0 text-[10px] ${STATUS_STYLE[openReport.status]}`} data-testid="detail-status">
                    {openReport.status_label}
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  {openReport.child_name} · {openReport.category_label} · submitted {fmt(openReport.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 text-sm">
                {openReport.incident_date && (
                  <p className="text-[#6B7280]"><span className="font-semibold text-[#1F2937]">Incident date:</span> {fmt(openReport.incident_date)}</p>
                )}
                {openReport.related_activity_label && (
                  <p className="text-[#6B7280] break-words"><span className="font-semibold text-[#1F2937]">Related:</span> {openReport.related_activity_label}</p>
                )}
                <div className="bg-[#F9FAFB] rounded-xl p-3.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-1.5">What you reported</p>
                  <p className="text-sm text-[#1F2937] whitespace-pre-wrap break-words">{openReport.description}</p>
                </div>

                {(openReport.updates || []).map((u, i) => (
                  <div key={i} className="bg-[#F9FAFB] rounded-xl p-3.5" data-testid={`detail-update-${i}`}>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-1.5">You added · {fmtTime(u.at)}</p>
                    <p className="text-sm text-[#1F2937] whitespace-pre-wrap break-words">{u.text}</p>
                  </div>
                ))}

                <div data-testid="detail-history">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">Status history</p>
                  <div className="space-y-1.5">
                    {(openReport.history || []).map((h, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-[#6B7280]">
                        <span className="text-[#9CA3AF] shrink-0 w-28">{fmtTime(h.at)}</span>
                        <span className="text-[#1F2937]">{h.action}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-[#F3F4F6] pt-3">
                  <Label>Add further information</Label>
                  <Textarea value={followUp} onChange={(e) => setFollowUp(e.target.value)} rows={3}
                            placeholder="Anything else the school should know"
                            className="mt-1 rounded-xl text-base sm:text-sm border-gray-200" data-testid="follow-up-input" />
                  <div className="flex flex-col sm:flex-row gap-2 mt-2">
                    <Button onClick={() => sendFollowUp(false)} disabled={followUp.trim().length < 5 || saving}
                            className="rounded-xl h-11 sm:h-9 bg-[#1F2937] hover:bg-[#374151] text-white text-xs flex-1"
                            data-testid="send-follow-up-btn">
                      Send to the school
                    </Button>
                    {openReport.status === 'closed' && (
                      <Button onClick={() => sendFollowUp(true)} disabled={followUp.trim().length < 5 || saving}
                              variant="outline" className="rounded-xl h-11 sm:h-9 text-xs flex-1 border-[#F97D6A] text-[#F97D6A] hover:bg-[#FFF0ED]"
                              data-testid="request-review-btn">
                        Request further review
                      </Button>
                    )}
                  </div>
                </div>

                <p className="text-[11px] text-[#9CA3AF] leading-relaxed">
                  The school's internal notes and investigation details are confidential and are not
                  shown here.
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
